import { Chess } from 'chess.js';
import type { Color, ImportResult, ParsedGame, VerifiedMove } from '../../types';

/**
 * Module de parsing PGN. Isolé du reste de l'application : il ne connaît
 * ni le moteur, ni le stockage, ni l'UI. Toute la validation des coups
 * passe par chess.js (source de vérité des règles).
 */

export class PgnParseError extends Error {
  constructor(
    message: string,
    public readonly gameIndex: number,
  ) {
    super(message);
    this.name = 'PgnParseError';
  }
}

/**
 * Découpe un texte pouvant contenir plusieurs parties PGN en blocs individuels.
 * Une nouvelle partie commence à un nouveau bloc d'en-têtes `[Clé "valeur"]`
 * qui suit la fin d'une partie précédente (marqueur de résultat).
 */
export function splitPgnGames(raw: string): string[] {
  const text = raw.replace(/\r\n/g, '\n').trim();
  if (!text) return [];

  const lines = text.split('\n');
  const blocks: string[] = [];
  let current: string[] = [];
  let sawMovetext = false;

  const isHeaderLine = (l: string) => /^\s*\[[A-Za-z0-9_]+\s+".*"\]\s*$/.test(l);

  for (const line of lines) {
    if (isHeaderLine(line) && sawMovetext && current.length > 0) {
      // Nouvelle partie détectée : on clôt le bloc courant.
      blocks.push(current.join('\n').trim());
      current = [];
      sawMovetext = false;
    }
    current.push(line);
    if (!isHeaderLine(line) && line.trim().length > 0) {
      sawMovetext = true;
    }
  }
  if (current.join('\n').trim().length > 0) {
    blocks.push(current.join('\n').trim());
  }
  return blocks.filter((b) => b.length > 0);
}

function extractVerifiedMoves(chess: Chess, startColorOverride?: Color): VerifiedMove[] {
  const verbose = chess.history({ verbose: true });
  const moves: VerifiedMove[] = [];
  let ply = 0;
  for (const m of verbose) {
    ply += 1;
    const moveNumber = Math.ceil(ply / 2);
    moves.push({
      ply,
      moveNumber,
      color: m.color as Color,
      san: m.san,
      uci: m.lan,
      fenBefore: m.before,
      fenAfter: m.after,
    });
  }
  void startColorOverride;
  return moves;
}

/** Hash simple (FNV-1a) pour dédoublonner sans dépendance externe. */
export function hashString(input: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

function normalizeForHash(pgn: string): string {
  return pgn.replace(/\s+/g, ' ').trim();
}

/**
 * Parse un unique bloc PGN. Lève PgnParseError avec un message compréhensible
 * si le PGN est invalide (syntaxe ou coup illégal).
 */
export function parseSingleGame(pgnBlock: string, gameIndex: number, isDemo = false): ParsedGame {
  const chess = new Chess();
  try {
    chess.loadPgn(pgnBlock, { strict: false });
  } catch (err) {
    const raw = err instanceof Error ? err.message : String(err);
    throw new PgnParseError(humanizePgnError(raw), gameIndex);
  }

  const headers = chess.getHeaders();
  const moves = extractVerifiedMoves(chess);

  if (moves.length === 0 && !hasSetupFen(headers)) {
    throw new PgnParseError(
      "Cette partie ne contient aucun coup. Vérifie que le fichier contient bien la liste des coups (pas seulement les en-têtes).",
      gameIndex,
    );
  }

  const result = headers.Result ?? chess.pgn().match(/(1-0|0-1|1\/2-1\/2|\*)\s*$/)?.[0] ?? '*';
  const id = hashString(normalizeForHash(pgnBlock));

  return {
    id,
    headers,
    moves,
    result,
    isDemo,
    sourceLabel: isDemo ? 'Partie de démonstration' : `Importée le ${new Date().toLocaleDateString('fr-FR')}`,
  };
}

function hasSetupFen(headers: Record<string, string>): boolean {
  return Boolean(headers.SetUp === '1' && headers.FEN);
}

/** Traduit les messages d'erreur techniques de chess.js en français compréhensible. */
function humanizePgnError(raw: string): string {
  if (/Invalid move/i.test(raw)) {
    const match = raw.match(/Invalid move(?: in PGN)?:\s*(.+)/i);
    const move = match?.[1]?.trim();
    return move
      ? `Coup illégal ou mal formé rencontré dans le PGN : « ${move} ». Vérifie la transcription de la partie.`
      : 'Un coup illégal ou mal formé a été trouvé dans ce PGN.';
  }
  if (/Expected/i.test(raw)) {
    return "Le format du PGN n'est pas reconnu (syntaxe invalide). Vérifie qu'il s'agit bien d'un fichier PGN standard.";
  }
  if (/Invalid FEN/i.test(raw)) {
    return "La position de départ (FEN) indiquée dans ce PGN est invalide.";
  }
  return `PGN invalide : ${raw}`;
}

/**
 * Point d'entrée principal : importe un texte pouvant contenir une ou
 * plusieurs parties. Les parties valides sont retournées ; les parties
 * invalides génèrent un avertissement mais n'empêchent pas l'import des
 * autres (une erreur ne doit jamais effacer les parties déjà valides).
 */
export function importPgnText(raw: string): ImportResult {
  const blocks = splitPgnGames(raw);
  if (blocks.length === 0) {
    return { games: [], warnings: ['Aucun contenu PGN détecté. Colle un PGN complet ou importe un fichier .pgn.'] };
  }

  const games: ParsedGame[] = [];
  const warnings: string[] = [];

  blocks.forEach((block, index) => {
    try {
      games.push(parseSingleGame(block, index));
    } catch (err) {
      if (err instanceof PgnParseError) {
        warnings.push(`Partie ${index + 1} : ${err.message}`);
      } else {
        warnings.push(`Partie ${index + 1} : erreur inattendue lors du parsing.`);
      }
    }
  });

  if (blocks.length > 1) {
    warnings.unshift(
      `Ce fichier contient ${blocks.length} parties. Sélectionne celle à analyser ci-dessous — Magnus analyse une partie à la fois en V1.`,
    );
  }

  return { games, warnings };
}

/** Devine la couleur jouée par l'utilisateur à partir des en-têtes et d'un pseudo connu. */
export function guessPlayerColor(headers: Record<string, string>, knownUsername?: string): Color | null {
  if (!knownUsername) return null;
  const normalized = knownUsername.trim().toLowerCase();
  if (headers.White?.trim().toLowerCase() === normalized) return 'w';
  if (headers.Black?.trim().toLowerCase() === normalized) return 'b';
  return null;
}
